import { useCallback, useEffect, useState } from 'react';
import type { ModelCamera, ModelLine, ModelPoint } from '../../types/crag-route';

/** Whether a line is complete enough to draw or save; one point is a tap. */
export function isDrawableModelLine(line: ModelLine | null | undefined): line is ModelLine {
  return !!line && line.points.length >= 2;
}

/**
 * Whether two cameras are the same one. With some slack, relative to how far the
 * camera stands: the orbit's own arithmetic moves it by a hair on every frame, and
 * that must not count as somebody having moved it.
 */
export function sameCamera(a: ModelCamera, b: ModelCamera | null | undefined): boolean {
  if (!b) return false;
  const gap = (p: ModelPoint, q: ModelPoint) => Math.hypot(p.x - q.x, p.y - q.y, p.z - q.z);
  const slack = gap(a.position, a.target) * 0.001;
  return gap(a.position, b.position) <= slack && gap(a.target, b.target) <= slack;
}

export type ModelLineEditor = ReturnType<typeof useModelLine>;

/**
 * The state of a line being drawn on a crag's 3D model: what `usePhotoLine` is to
 * a photo, with the same gestures behind it, and with the pointer left out. Which
 * point of the rock a press means is three.js's to say (`CragRouteModel`), and
 * this file imports none of it, because the panel's controls use it too and the
 * page must not pull three into its own bundle.
 */
export function useModelLine({
  frame,
  line,
  onChange,
}: {
  /** The coordinate system of the model being drawn on; see Crag#model_3d_frame. */
  frame: string | null;
  line: ModelLine | null;
  onChange: (line: ModelLine | null) => void;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const points = line?.points ?? [];

  const clearSelection = useCallback(() => setSelected(null), []);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') clearSelection();
    }

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [clearSelection]);

  // A selection is an index, so a shorter line would leave it pointing past the
  // end, and the delete button would look armed while removing nothing.
  useEffect(() => {
    setSelected((current) => (current !== null && current >= points.length ? null : current));
  }, [points.length]);

  function emit(next: ModelPoint[]) {
    onChange(next.length === 0 || !frame ? null : { ...line, frame, points: next });
  }

  return {
    points,
    selected,
    setSelected,
    clearSelection,
    /** Carries the line on to a point on the rock. */
    add(point: ModelPoint) {
      emit([...points, point]);
      setSelected(points.length);
    },
    /** Makes a point at `index`, pushing the later ones along. */
    insert(index: number, point: ModelPoint) {
      const next = points.slice();
      next.splice(index, 0, point);
      emit(next);
      setSelected(index);
    },
    move(index: number, point: ModelPoint) {
      emit(points.map((current, at) => (at === index ? point : current)));
    },
    deleteSelected() {
      if (selected === null) return;
      emit(points.filter((_, at) => at !== selected));
      setSelected(null);
    },
    undo() {
      emit(points.slice(0, -1));
    },
    clearLine() {
      onChange(null);
      setSelected(null);
    },
  };
}
